import GNEAsyncPagination from 'components/shared/dropdown-with-pagination/GNEAsyncPagination';
import OverlayTrigger from 'components/shared/tooltip/Tooltip';
import {
  ALL,
  BOOLEAN_FALSE,
  BOOLEAN_TRUE,
  EcosystemOpportunityField,
  EMPTY_ARRAY,
  EMPTY_STRING,
  NUMBER_ZERO,
  UNDEFINED,
} from 'constants/constants';
import { useRef, useState } from 'react';
import { Col, Row, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { wrapMenuList } from 'react-select-async-paginate';
import { loadDropdownOption } from 'store/customer/customer-view-actions';
import {
  buildDropdownExpression,
  dropdownOptions,
  formatOptionLabel,
  getFormatedOptionLabel,
  isValidSearchText,
  ValueContainer,
} from 'utils/utils';
import classes from '../ecosystem-opportunity/ecosytemOpportunity.module.scss';

import { useQueryClient } from '@tanstack/react-query';

const EcosystemOpportunityFilter = ({
  handleFilterChange,
  selectedEcoOppProduct,
  selectedEcoOppIndication,
  selectedEcoOppCompetitorProduct,
  selectedEcoOppBookOfBusiness,
  selectedEcoOppBenefitType,
  selectedEcoOppRegion,
  selectedEcoOppEcosystemName,
}) => {
  const firstLoading = useRef(BOOLEAN_TRUE);
  const shouldLoadMore = useRef(BOOLEAN_FALSE);
  const selectedCustomer = useSelector(
    (state) => state?.CustomerView?.selectedCustomer
  );
  const [offset, setOffset] = useState(NUMBER_ZERO);

  const handleOnChange = (value, field) => {
    handleFilterChange(value, field);
  };

  const shouldLoadMoreOptions = (scrollHeight, clientHeight, scrollTop) => {
    if (firstLoading.current) {
      firstLoading.current = BOOLEAN_FALSE;
      return BOOLEAN_TRUE;
    }
    const bottomBorder = (scrollHeight - clientHeight) / 2;
    const loadMore = bottomBorder < scrollTop;
    shouldLoadMore.current = loadMore;
    return loadMore;
  };

  const queryClient = useQueryClient();

  const loadOptions = async (inputValue, fieldName, dependency) => {
    if (!isValidSearchText(inputValue)) {
      return {
        options: EMPTY_ARRAY,
      };
    }

    // Build Expression
    const expression = buildDropdownExpression(dependency);
    const params = {
      offset: firstLoading.current
        ? NUMBER_ZERO
        : !shouldLoadMore.current
          ? NUMBER_ZERO
          : offset,
      mdmId: selectedCustomer?.value,
      entityType: selectedCustomer?.flag,
      fieldName,
      searchText: inputValue === EMPTY_STRING ? UNDEFINED : inputValue,
      expression: expression === EMPTY_STRING ? UNDEFINED : expression,
    };

    // Compose a unique query key for caching
    const queryKey = ['EcosystemOpportunityFilter-' + fieldName, params];

    // Try to get cached data first
    let data = queryClient.getQueryData(queryKey);

    if (!data) {
      // Fetch and cache
      data = await loadDropdownOption(params);
      queryClient.setQueryData(queryKey, data);
    }

    const { values, currentListSize, hasMoreElement } = data || {
      values: [],
      currentListSize: 0,
      hasMoreElement: false,
    };

    if (hasMoreElement) {
      setOffset((prevValue) => prevValue + currentListSize);
    }
    return {
      // Extract address, label and value from label
      options: getFormatedOptionLabel(values),
      hasMore: hasMoreElement,
    };
  };

  return (
    <div className='mt-3'>
      <Row>
        <Col>
          <label className='d-flex align-items-center'>
            <span className={`text text__bold badge-circle`}>1</span>
            <span className={`text text__bold text__blue text__normal`}>
              Product
            </span>
            <OverlayTrigger
              overlay={
                <Tooltip className='gne-tooltip'>
                  Products outside of AHR, Immunology, Multiple Sclerosis,
                  Ophtha, and Respiratory TAs do not yet have market share
                  methodologies defined. Selecting these products will not show
                  market share metrics.
                </Tooltip>
              }>
              <i className='icon icon__info__gray ms-2'></i>
            </OverlayTrigger>
          </label>
          <GNEAsyncPagination
            value={selectedEcoOppProduct}
            onChange={(value) => {
              handleOnChange(value, EcosystemOpportunityField.Product);
            }}
            placeholder={ALL}
            isMulti={BOOLEAN_FALSE}
            closeMenuOnSelect={BOOLEAN_FALSE}
            isClearable={BOOLEAN_TRUE}
            loadOptions={(searchValue) => {
              return loadOptions(
                searchValue,
                EcosystemOpportunityField.Product,
                {
                  [EcosystemOpportunityField.BenefitType]:
                    selectedEcoOppBenefitType,
                  [EcosystemOpportunityField.BookOfBusiness]:
                    selectedEcoOppBookOfBusiness,
                  [EcosystemOpportunityField.Indication]:
                    selectedEcoOppIndication,
                  [EcosystemOpportunityField.CompetitorBrand]:
                    selectedEcoOppCompetitorProduct,
                }
              );
            }}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            formatOptionLabel={(data, { context }) =>
              formatOptionLabel(data, { context })
            }
            cacheUniqs={[
              selectedEcoOppBenefitType,
              selectedEcoOppIndication,
              selectedEcoOppCompetitorProduct,
              selectedEcoOppBookOfBusiness,
              selectedCustomer,
            ]}
          />
        </Col>
        <Col>
          <label className='d-flex align-items-center'>
            <span className={`text text__bold badge-circle`}>2</span>
            <span className={`text text__bold text__blue text__normal`}>
              Indication
            </span>
            <OverlayTrigger
              overlay={
                <Tooltip className='gne-tooltip'>
                  Values depend on the selected Product and Benefit Type.
                  Indication options will only appear after choosing a single
                  Product. Competitor Brand will populate based on the
                  Product/Indication combination.
                </Tooltip>
              }>
              <i className='icon icon__info__gray ms-2'></i>
            </OverlayTrigger>
          </label>
          <GNEAsyncPagination
            value={selectedEcoOppIndication}
            onChange={(value) => {
              handleOnChange(value, EcosystemOpportunityField.Indication);
            }}
            placeholder={'Select Indication'}
            isMulti={BOOLEAN_FALSE}
            closeMenuOnSelect={BOOLEAN_FALSE}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(
                searchValue,
                EcosystemOpportunityField.Indication,
                {
                  [EcosystemOpportunityField.Product]: selectedEcoOppProduct,
                  [EcosystemOpportunityField.BenefitType]:
                    selectedEcoOppBenefitType,
                  [EcosystemOpportunityField.BookOfBusiness]:
                    selectedEcoOppBookOfBusiness,
                  [EcosystemOpportunityField.CompetitorBrand]:
                    selectedEcoOppCompetitorProduct,
                }
              );
            }}
            isDisabled={!selectedEcoOppProduct?.value}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            formatOptionLabel={(data, { context }) =>
              formatOptionLabel(data, { context })
            }
            cacheUniqs={[
              selectedEcoOppProduct,
              selectedEcoOppBenefitType,
              selectedEcoOppCompetitorProduct,
              selectedEcoOppBookOfBusiness,
              selectedCustomer,
            ]}
          />
        </Col>
        <Col>
          <label className='d-flex align-items-center text-nowrap'>
            <span className={`text text__bold badge-circle`}>3</span>
            <span className={`text text__bold text__blue text__normal`}>
              Competitor Brand
            </span>
            <OverlayTrigger
              overlay={
                <Tooltip className='gne-tooltip'>
                  Select a Competitor Brand to view Payer Access Position.
                </Tooltip>
              }>
              <i className='icon icon__info__gray ms-2'></i>
            </OverlayTrigger>
          </label>
          <GNEAsyncPagination
            value={selectedEcoOppCompetitorProduct}
            onChange={(value) => {
              handleOnChange(value, EcosystemOpportunityField.CompetitorBrand);
            }}
            placeholder={'Select Competitor Brand'}
            isMulti={BOOLEAN_FALSE}
            closeMenuOnSelect={BOOLEAN_FALSE}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(
                searchValue,
                EcosystemOpportunityField.CompetitorBrand,
                {
                  [EcosystemOpportunityField.Indication]:
                    selectedEcoOppIndication,
                  [EcosystemOpportunityField.Product]: selectedEcoOppProduct,
                  [EcosystemOpportunityField.BenefitType]:
                    selectedEcoOppBenefitType,
                  [EcosystemOpportunityField.BookOfBusiness]:
                    selectedEcoOppBookOfBusiness,
                }
              );
            }}
            isDisabled={
              !selectedEcoOppProduct?.value || !selectedEcoOppIndication?.value
            }
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            formatOptionLabel={(data, { context }) =>
              formatOptionLabel(data, { context })
            }
            cacheUniqs={[
              selectedEcoOppBenefitType,
              selectedEcoOppIndication,
              selectedEcoOppProduct,
              selectedEcoOppBookOfBusiness,
              selectedCustomer,
            ]}
          />
        </Col>
        <Col>
          <label className='d-flex align-items-center text-nowrap'>
            <span className={`text text__bold badge-circle`}>4</span>
            <span className={`text text__bold text__blue text__normal`}>
              Book of Business
            </span>
            <OverlayTrigger
              overlay={
                <Tooltip className='gne-tooltip'>
                  The Book of Business filter will affect the calculation of
                  Payer Contribution, Ecosystem Sales, Payer Specific Market
                  Share, Market Share, and Relative Access Position
                </Tooltip>
              }>
              <i className='icon icon__info__gray ms-2'></i>
            </OverlayTrigger>
          </label>
          <GNEAsyncPagination
            value={selectedEcoOppBookOfBusiness}
            onChange={(value) => {
              handleOnChange(value, EcosystemOpportunityField.BookOfBusiness);
            }}
            placeholder={ALL}
            isMulti={BOOLEAN_FALSE}
            closeMenuOnSelect={BOOLEAN_FALSE}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(
                searchValue,
                EcosystemOpportunityField.BookOfBusiness,
                {
                  [EcosystemOpportunityField.Product]: selectedEcoOppProduct,
                  [EcosystemOpportunityField.Indication]:
                    selectedEcoOppIndication,
                  [EcosystemOpportunityField.CompetitorBrand]:
                    selectedEcoOppCompetitorProduct,
                  [EcosystemOpportunityField.BenefitType]:
                    selectedEcoOppBenefitType,
                }
              );
            }}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            formatOptionLabel={(data, { context }) =>
              formatOptionLabel(data, { context })
            }
            cacheUniqs={[
              selectedEcoOppProduct,
              selectedEcoOppBenefitType,
              selectedEcoOppIndication,
              selectedEcoOppCompetitorProduct,
              selectedCustomer,
            ]}
          />
        </Col>
        <Col>
          <label className='d-flex align-items-center text-nowrap'>
            <span className={`text text__bold badge-circle`}>5</span>
            <span className={`text text__bold text__blue text__normal`}>
              Benefit Type
            </span>
          </label>
          <GNEAsyncPagination
            value={selectedEcoOppBenefitType}
            onChange={(value) => {
              handleOnChange(value, EcosystemOpportunityField.BenefitType);
            }}
            placeholder={ALL}
            isMulti={BOOLEAN_FALSE}
            closeMenuOnSelect={BOOLEAN_FALSE}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(
                searchValue,
                EcosystemOpportunityField.BenefitType,
                {
                  [EcosystemOpportunityField.Product]: selectedEcoOppProduct,
                  [EcosystemOpportunityField.Indication]:
                    selectedEcoOppIndication,
                  [EcosystemOpportunityField.CompetitorBrand]:
                    selectedEcoOppCompetitorProduct,
                  [EcosystemOpportunityField.BookOfBusiness]:
                    selectedEcoOppBookOfBusiness,
                }
              );
            }}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            formatOptionLabel={(data, { context }) =>
              formatOptionLabel(data, { context })
            }
            cacheUniqs={[
              selectedEcoOppProduct,
              selectedEcoOppIndication,
              selectedEcoOppCompetitorProduct,
              selectedEcoOppBookOfBusiness,
              selectedCustomer,
            ]}
          />
        </Col>
      </Row>

      <Row className={`mt-2 ${classes.ecoOppFilterSecondRow}`}>
        <Col md={6}>
          <label className='title__x-small'>Region</label>{' '}
          <OverlayTrigger
            overlay={
              <Tooltip className='gne-tooltip'>
                The Region filter limits the number of Ecosystems viewable in
                the tables and graphs to those aligned to the selected Region
              </Tooltip>
            }>
            <i className='icon icon__info__gray ms-2'></i>
          </OverlayTrigger>
          <GNEAsyncPagination
            value={selectedEcoOppRegion}
            onChange={(value) => {
              handleOnChange(value, EcosystemOpportunityField.Region);
            }}
            placeholder={ALL}
            isMulti={BOOLEAN_TRUE}
            closeMenuOnSelect={BOOLEAN_FALSE}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(
                searchValue,
                EcosystemOpportunityField.Region,
                {
                  [EcosystemOpportunityField.Ecosystem]:
                    selectedEcoOppEcosystemName,
                }
              );
            }}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            formatOptionLabel={(data, { context }) =>
              formatOptionLabel(data, { context })
            }
            cacheUniqs={[selectedCustomer, selectedEcoOppEcosystemName]}
          />
        </Col>
        <Col md={6}>
          <label className='title__x-small'>Ecosystem</label>{' '}
          <OverlayTrigger
            overlay={
              <Tooltip className='gne-tooltip'>
                The Ecosystem filter limits the number of Ecosystems viewable in
                the tables and graphs
              </Tooltip>
            }>
            <i className='icon icon__info__gray ms-2'></i>
          </OverlayTrigger>
          <GNEAsyncPagination
            value={selectedEcoOppEcosystemName}
            onChange={(value) => {
              handleOnChange(value, EcosystemOpportunityField.Ecosystem);
            }}
            placeholder={ALL}
            isMulti={BOOLEAN_TRUE}
            closeMenuOnSelect={BOOLEAN_FALSE}
            isClearable
            loadOptions={(searchValue) => {
              return loadOptions(
                searchValue,
                EcosystemOpportunityField.Ecosystem,
                {
                  [EcosystemOpportunityField.Region]: selectedEcoOppRegion,
                }
              );
            }}
            shouldLoadMore={shouldLoadMoreOptions}
            components={{
              ValueContainer: ValueContainer,
              Option: wrapMenuList(dropdownOptions),
            }}
            formatOptionLabel={(data, { context }) =>
              formatOptionLabel(data, { context })
            }
            cacheUniqs={[selectedEcoOppRegion, selectedCustomer]}
          />
        </Col>
      </Row>
    </div>
  );
};

export default EcosystemOpportunityFilter;
